import { throwIfAborted } from '../codecs/types';
import { DEFAULT_LIMITS, validateDimensions, walkLayers, type EditorDocument, type Layer } from '../core/model';
import { serializeProject } from '../persistence/project-format';
import { admitPortableTextSnapshot, createPortableTextSnapshot } from '../vector/portable-text-snapshot';
import type { PortableTextLayoutInput } from '../vector/portable-text-layout';
import { editorDocumentToPublicScene, publicSceneToEditorDocument } from './adapter';
import { activateNativeText, nativeDocumentTextPaint, prepareNativeTextEdit } from './native-text';
import { prepareNativeDocumentResources, releaseNativeDocumentResources } from './native-resources';
import { parsePortableScene, PORTABLE_SCENE_SCHEMA, PORTABLE_SCENE_VERSION, verifyPortableSceneAssets, type PortableScene, type PortableSceneNode, type PortableSceneTextNode } from './portable-scene';
import { sceneSupportDisposition } from './validation';
import type { NativePortableTextPayload } from './native-text-contract';
import type { PublicNode } from './types';

/** Public snapshots bind directly to public IDs, independent of Studio's native namespace. */
function layoutInput(scene: PortableScene, node: PortableSceneTextNode): PortableTextLayoutInput {
  const settings = node.layout;
  const fonts = settings.fonts.map(fontRef => {
    const font = scene.fonts.find(font => font.id === fontRef)!;
    if (font.source.kind !== 'embedded') throw new Error('Portable font must be embedded.');
    const assetId = font.source.asset; const asset = scene.assets.find(asset => asset.id === assetId)!;
    return { fontRef, fontBytes: Uint8Array.from(atob(asset.data), character => character.charCodeAt(0)) };
  });
  return { documentId: scene.documentId, nodeId: node.id, revision: settings.revision, text: node.text, fonts,
    fontSize: node.style.fontSize, width: node.frame.width, topLeft: { x: 0, y: 0 }, metricsPolicy: settings.metricsPolicy,
    lineGap: settings.lineGap, overflow: settings.overflow, emptyLineFontIndex: 0, baseDirection: settings.baseDirection,
    language: settings.language, scriptLanguages: structuredClone(settings.scriptLanguages) };
}
async function publicIdentity(value: string, kind: 'document' | 'node'): Promise<string> {
  if (/^[A-Za-z0-9][A-Za-z0-9._:/-]{0,127}$/.test(value)) return value;
  for (let index = 0; index < value.length; index++) {
    const unit = value.charCodeAt(index);
    if (unit >= 0xd800 && unit <= 0xdbff) {
      const low = value.charCodeAt(++index);
      if (!(low >= 0xdc00 && low <= 0xdfff)) throw new Error('Public identities require valid Unicode.');
    } else if (unit >= 0xdc00 && unit <= 0xdfff) throw new Error('Public identities require valid Unicode.');
  }
  const digest = new Uint8Array(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(value)));
  return `draft.${kind}.` + [...digest].map(byte => byte.toString(16).padStart(2, '0')).join('');
}
async function verifyAssets(scene: PortableScene, signal?: AbortSignal): Promise<void> {
  const errors = (await verifyPortableSceneAssets(scene)).filter(item => item.severity === 'error');
  if (errors.length) throw new Error('Portable scene assets failed verification.');
  throwIfAborted(signal);
}
function textNodes(nodes: PortableSceneNode[]): PortableSceneTextNode[] {
  return nodes.flatMap(node => node.type === 'group' ? textNodes(node.children) : node.type === 'portable-text' ? [node] : []);
}

/** Capture before yielding; rebuild identity-bound public snapshots without source mutation. */
export async function editorDocumentToPortableScene(document: EditorDocument, signal?: AbortSignal): Promise<PortableScene> {
  throwIfAborted(signal);
  for (const layer of [document.root, ...walkLayers(document.root)]) {
    // Reject unsupported tile-bearing content before copying potentially large
    // pixel buffers. Selection/history are not part of a public projection.
    if (layer.kind === 'raster' || layer.kind === 'adjustment') throw new Error(`Portable scene export does not support a ${layer.kind} layer.`);
    if (layer.mask) throw new Error('Portable scene export does not support a layer mask.');
    if (layer.kind === 'text' && layer.text.schemaVersion === 2) nativeDocumentTextPaint(document, layer);
  }
  const captured = { ...document, color: { ...document.color }, root: structuredClone(document.root), extensions: structuredClone(document.extensions) };
  captured.id = await publicIdentity(captured.id, 'document');
  for (const layer of walkLayers(captured.root)) {
    // The base adapter consumes this internal layer as canvas.background. Its
    // reserved identity is never emitted as a public node.
    if (layer.id !== '@maipo:canvas-background') layer.id = await publicIdentity(layer.id, 'node');
  }
  throwIfAborted(signal);
  const layouts = new Map<string, NativePortableTextPayload>();
  const project = (layer: Layer): Layer => {
    if (layer.kind === 'group') return { ...layer, children: layer.children.map(project) };
    if (layer.kind !== 'text' || layer.text.schemaVersion !== 2) return layer;
    const layout = structuredClone(layer.text.portable!); layouts.set(layer.id, layout);
    const text = { ...layer.text, schemaVersion: 1 as const }; delete text.portable;
    return { ...layer, text, extensions: { ...layer.extensions, 'maipo:public-font-ref': layout.fonts[0] } };
  };
  const base = editorDocumentToPublicScene({ ...captured, root: { ...captured.root, children: captured.root.children.map(project) } }, { draftImages: true });
  const unsupported = sceneSupportDisposition(base).unsupported[0];
  if (unsupported) throw new Error(`Unsupported required extension ${unsupported.key}@${unsupported.version}.`);
  const restore = (node: PublicNode): PortableSceneNode => {
    if (node.type === 'group') return { ...node, children: node.children.map(restore) };
    const layout = layouts.get(node.id);
    return node.type === 'text' && layout ? { ...node, type: 'portable-text', layout } : node;
  };
  const scene = parsePortableScene({ ...base, $schema: PORTABLE_SCENE_SCHEMA, version: PORTABLE_SCENE_VERSION, children: base.children.map(restore) });
  await verifyAssets(scene, signal);
  for (const node of textNodes(scene.children)) node.layout.snapshot = (await createPortableTextSnapshot(layoutInput(scene, node), signal)).serialized;
  throwIfAborted(signal);
  return scene;
}

/** Admit public identities/layout first, then prepare Studio's distinct native binding. */
export async function portableSceneToEditorDocument(value: unknown, signal?: AbortSignal): Promise<EditorDocument> {
  throwIfAborted(signal);
  const scene = parsePortableScene(value);
  const layouts = new Map<string, NativePortableTextPayload>();
  const project = (node: PortableSceneNode): PublicNode => {
    if (node.type === 'group') return { ...node, children: node.children.map(project) };
    if (node.type !== 'portable-text') return node;
    const { layout, ...base } = node; layouts.set(node.id, layout);
    return { ...base, type: 'text' };
  };
  const base: Record<string, unknown> = { ...scene }; delete base.$schema;
  const document = publicSceneToEditorDocument({ ...base, version: '0.1', children: scene.children.map(project) }, { draftImages: true });
  validateDimensions(document.width, document.height);
  if (walkLayers(document.root).length > DEFAULT_LIMITS.maxLayers) throw new Error('Portable scene exceeds the Studio layer limit.');
  try {
    await verifyAssets(scene, signal);
    for (const node of textNodes(scene.children)) await admitPortableTextSnapshot(node.layout.snapshot, layoutInput(scene, node), signal);
    for (const layer of walkLayers(document.root)) {
      const layout = layouts.get(layer.id);
      if (layer.kind !== 'text' || !layout) continue;
      layer.text.schemaVersion = 2; layer.text.portable = layout;
      const prepared = await prepareNativeTextEdit(document, layer, signal);
      try { layer.text = prepared.text; activateNativeText(document, layer, prepared.lease.state); }
      finally { prepared.lease.release(); }
    }
    await prepareNativeDocumentResources(document, signal);
    await serializeProject(document, signal);
    throwIfAborted(signal);
    return document;
  } catch (error) { releaseNativeDocumentResources(document); throw error; }
}
